import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

// ── Domain Intel ──────────────────────────────────────────────────

export interface DomainOverview {
  organic: {
    count: number
    etv: number
    estimated_paid_traffic_cost: number
    pos_1: number
    pos_2_3: number
    pos_4_10: number
    pos_11_20: number
    pos_21_30: number
    pos_31_40: number
    pos_41_50: number
    pos_51_60: number
    pos_61_70: number
    pos_71_80: number
    pos_81_90: number
    pos_91_100: number
  }
  paid: {
    count: number
    etv: number
    estimated_paid_traffic_cost: number
  }
}

export interface RankedKeyword {
  keyword: string
  position: number
  search_volume: number | null
  cpc: number | null
  competition: number | null
  url: string | null
  etv: number | null
  serp_features: string[]
}

export function useDomainOverview(siteId: string) {
  return useQuery({
    queryKey: ['labs', 'overview', siteId],
    queryFn: () => apiClient.get<{ overview: DomainOverview | null }>(`/api/sites/${siteId}/labs/overview`),
    staleTime: 30 * 60 * 1000,
    enabled: !!siteId,
  })
}

export function useRankedKeywords(siteId: string, maxPosition?: string, sort = 'position,asc', offset = 0) {
  const params = new URLSearchParams()
  if (maxPosition) params.set('maxPosition', maxPosition)
  if (sort) params.set('sort', sort)
  if (offset) params.set('offset', String(offset))
  const qs = params.toString()

  return useQuery({
    queryKey: ['labs', 'ranked-keywords', siteId, maxPosition, sort, offset],
    queryFn: () => apiClient.get<{
      totalCount: number
      items: RankedKeyword[]
      trackedKeywords: string[]
    }>(`/api/sites/${siteId}/labs/ranked-keywords${qs ? `?${qs}` : ''}`),
    staleTime: 30 * 60 * 1000,
    enabled: !!siteId,
  })
}

export function useImportKeywords(siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (keywords: string[]) =>
      apiClient.post<{ added: number }>(`/api/sites/${siteId}/labs/import-keywords`, { keywords }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['keywords', siteId] })
      queryClient.invalidateQueries({ queryKey: ['labs', 'ranked-keywords', siteId] })
    },
  })
}

// ── Keyword Research ──────────────────────────────────────────────

export interface KeywordSuggestion {
  keyword: string
  search_volume: number | null
  cpc: number | null
  competition: number | null
  competition_level: string | null
  monthly_searches: Array<{ year: number; month: number; search_volume: number }> | null
}

export interface SearchIntentResult {
  keyword: string
  intent: string
  probability: number
}

export function useKeywordSuggestions(siteId: string, keyword: string) {
  return useQuery({
    queryKey: ['labs', 'suggestions', siteId, keyword],
    queryFn: () => apiClient.get<{
      suggestions: KeywordSuggestion[]
      trackedKeywords: string[]
    }>(`/api/sites/${siteId}/labs/keyword-suggestions?keyword=${encodeURIComponent(keyword)}`),
    staleTime: 30 * 60 * 1000,
    enabled: !!siteId && !!keyword,
  })
}

export function useSearchIntent(siteId: string) {
  return useMutation({
    mutationFn: (keywords: string[]) =>
      apiClient.post<{ intents: SearchIntentResult[] }>(`/api/sites/${siteId}/labs/search-intent`, { keywords }),
  })
}

// ── Competitor Discovery ──────────────────────────────────────────

export interface DiscoveredCompetitor {
  domain: string
  avg_position: number
  intersections: number
  etv: number
  keywords_count: number
}

export function useDiscoverCompetitors(siteId: string, enabled = false) {
  return useQuery({
    queryKey: ['labs', 'competitors', siteId],
    queryFn: () => apiClient.get<{ competitors: DiscoveredCompetitor[] }>(`/api/sites/${siteId}/labs/competitors`),
    staleTime: 30 * 60 * 1000,
    enabled: !!siteId && enabled,
  })
}

// ── Keyword Gap Analysis ──────────────────────────────────────────

export interface GapKeyword {
  keyword: string
  search_volume: number | null
  cpc: number | null
  competition: number | null
  intent: string | null
  competitor_position: number | null
  our_position: number | null
}

export function useKeywordGap(siteId: string, competitorDomain: string, limit = 100) {
  return useQuery({
    queryKey: ['labs', 'gap', siteId, competitorDomain, limit],
    queryFn: () => apiClient.get<{ keywords: GapKeyword[] }>(
      `/api/sites/${siteId}/labs/gap?competitorDomain=${encodeURIComponent(competitorDomain)}&limit=${limit}`
    ),
    staleTime: 30 * 60 * 1000,
    enabled: !!siteId && !!competitorDomain,
  })
}

// ── Technology Detection ────────────────────────────────────────

export interface TechItem {
  name: string
  category: string
}

export function useTechnologies(siteId: string) {
  return useQuery({
    queryKey: ['labs', 'technologies', siteId],
    queryFn: () => apiClient.get<{ technologies: TechItem[] }>(`/api/sites/${siteId}/labs/technologies`),
    staleTime: 30 * 60 * 1000,
    enabled: !!siteId,
  })
}
